import React from 'react';
import { AudienceLevel, Cohort, LEVEL_LABELS } from '../../types/pathway';
import { moduleById } from '../../data/skillModules';
import { formatHours, totalHours } from '../../utils/pathwayMatching';
interface PathwayDocumentProps {
  pathwayName: string;
  company: string;
  cohort: Cohort | null;
  audience: AudienceLevel | 'any';
  /** Module ids in the order the prospect should take them. */
  items: string[];
  /** Optional field labels the rep ticked in the send dialog. */
  fields?: string[];
  /** Render only the cover page — used for the thumbnail in the send dialog. */
  coverOnly?: boolean;
}
const MODULES_PER_PAGE = 3;
export function pageCount(itemCount: number): number {
  return 1 + Math.max(1, Math.ceil(itemCount / MODULES_PER_PAGE));
}
function chunk(items: string[]): string[][] {
  const pages: string[][] = [];
  for (let i = 0; i < items.length; i += MODULES_PER_PAGE) {
    pages.push(items.slice(i, i + MODULES_PER_PAGE));
  }
  return pages;
}
function Page({
  children,
  footerLabel,
  pageNumber,
  totalPages
}: {
  children: React.ReactNode;
  footerLabel: string;
  pageNumber: number;
  totalPages: number;
}) {
  return <div className="pathway-page flex h-[1056px] w-[816px] flex-col bg-white shadow-lg" data-id="element-1599" data-mp="2yim4n0n03xa5">
      <div className="h-2 w-full flex-shrink-0 bg-[#DB3725]" data-id="element-1600" data-mp="33gf7bjp3i95o" />
      <div className="flex flex-1 flex-col px-16 py-12" data-id="element-1601" data-mp="38eyx5v6t853b">{children}</div>
      <div className="flex flex-shrink-0 items-center justify-between border-t border-gray-200 px-16 py-5" data-id="element-1602" data-mp="2ar8b4rd45vnl">
        <span className="text-[10px] text-gray-500" data-id="element-1603" data-mp="2fpi7346nx0a9">{footerLabel}</span>
        <span className="text-[10px] text-gray-500" data-id="element-1604" data-mp="2fnoy3i8ku3z3">
          Page {pageNumber} of {totalPages}
        </span>
      </div>
    </div>;
}
function Wordmark() {
  return <div className="flex items-baseline gap-2" data-id="element-1605" data-mp="2phfj2d5hvwa1">
      <span className="text-lg font-bold text-[#DB3725]" data-id="element-1606" data-mp="1s0qisnw7122r">
        University of Phoenix
      </span>
      <span className="text-sm font-semibold text-gray-700" data-id="element-1607" data-mp="1wza5iqim2hw6">
        Professional Development
      </span>
    </div>;
}
export function PathwayDocument({
  pathwayName,
  company,
  cohort,
  audience,
  items,
  fields = [],
  coverOnly = false
}: PathwayDocumentProps) {
  const showCategory = fields.includes('Category');
  const showDuration = fields.includes('Duration');
  const showTopics = fields.includes('Topics');
  const showModuleId = fields.includes('Skill Module ID');
  const contentPages = chunk(items);
  const totalPages = contentPages.length + 1;
  const recipient = company.trim() || 'your team';
  const today = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  const footerLabel = `${pathwayName || 'Recommended pathway'} · Prepared for ${recipient}`;
  return <div className="flex flex-col items-center gap-8" data-id="element-1608" data-mp="1axi4cim5n5w8">
      <Page footerLabel="University of Phoenix · Professional Development" pageNumber={1} totalPages={totalPages} data-id="element-1609" data-mp="2rctcc5lhapge">
        <Wordmark data-id="element-1610" data-mp="2mdzzmfe0n2ha" />

        <div className="mt-20" data-id="element-1611" data-mp="2mgm4265m11va">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#30718D]" data-id="element-1612" data-mp="85z0hr1mkwgf">
            Recommended learning pathway
          </div>
          <h1 className="mt-3 text-[40px] font-bold leading-tight text-gray-900" data-id="element-1613" data-mp="d6v381a8qck9">
            {pathwayName || 'Recommended Learning Pathway'}
          </h1>
          <p className="mt-4 text-lg text-gray-700" data-id="element-1614" data-mp="443tq70xdqv9">
            Prepared for {recipient}
            {cohort ? ` · ${cohort.name}` : ''}
          </p>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1615" data-mp="rwu102xvvlz7">{today}</p>
        </div>

        <div className="mt-12 grid grid-cols-3 gap-6 border-y border-gray-200 py-6" data-id="element-1616" data-mp="vvjk8c5lrxqr">
          <div data-id="element-1617" data-mp="i3vdkoe4i1h6">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1618" data-mp="i4ejf6qi0zeu">
              Built for
            </div>
            <div className="mt-1 text-2xl font-bold text-gray-900" data-id="element-1619" data-mp="1atlsgznkx7ec">
              {audience === 'any' ? 'Mixed' : LEVEL_LABELS[audience]}
            </div>
          </div>
          <div data-id="element-1620" data-mp="1fm54vtaj5whk">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1621" data-mp="3n3lvy3ymtbsu">
              Modules
            </div>
            <div className="mt-1 text-2xl font-bold text-gray-900" data-id="element-1622" data-mp="3i497xfw0xyoo">
              {items.length}
            </div>
          </div>
          <div data-id="element-1623" data-mp="3e3gxijbf1nqt">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1624" data-mp="3e51lfq3868fp">
              Total time
            </div>
            <div className="mt-1 text-2xl font-bold text-gray-900" data-id="element-1625" data-mp="9p8pzeqi06eo">
              {formatHours(totalHours(items))}
            </div>
          </div>
        </div>

        <p className="mt-8 max-w-[34rem] text-sm leading-relaxed text-gray-700" data-id="element-1626" data-mp="5p8bju3efv7m">
          This pathway was built from our conversation about{' '}
          {recipient === 'your team' ? 'your team' : recipient} and is listed in
          the order we recommend taking it. It is designed for{' '}
          <span className="font-semibold" data-id="element-1627" data-mp="teszkreetinr">
            {audience === 'any' ? 'a mixed-experience group' : LEVEL_LABELS[audience].toLowerCase()}
          </span>
          .
        </p>

        <div className="mt-10" data-id="element-1628" data-mp="10w7m30s33f63">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1629" data-mp="10vfqvr2efjgc">
            At a glance
          </div>
          <ol className="mt-3 space-y-2" data-id="element-1630" data-mp="15tr0q1sp1tyk">
            {items.map((moduleId, index) => {
            const module = moduleById(moduleId);
            if (!module) return null;
            return <li key={moduleId} className="flex items-baseline gap-3" data-id="element-1631" data-mp="1evqhcszsqw0z" data-mpi={index}>
                  <span className="w-5 flex-shrink-0 text-xs font-bold text-[#30718D]" data-id="element-1632" data-mp="1ju1tjadkdimd">
                    {index + 1}
                  </span>
                  <span className="flex-1 text-sm font-semibold text-gray-900" data-id="element-1633" data-mp="1oo603six3omx">
                    {module.name}
                  </span>
                  <span className="flex-shrink-0 text-xs text-gray-500" data-id="element-1634" data-mp="1xn9jyypws82h">
                    {formatHours(module.durationHours)}
                  </span>
                </li>;
          })}
          </ol>
        </div>

        <div className="mt-auto pt-10" data-id="element-1635" data-mp="2jv06y39swdo">
          <p className="text-[10px] leading-relaxed text-gray-500" data-id="element-1636" data-mp="3un4d1h1q58qv">
            Prepared by University of Phoenix Professional Development. Module
            content and availability are subject to change. Contact your account
            team for enrollment and pricing.
          </p>
        </div>
      </Page>

      {!coverOnly && contentPages.map((page, pageIndex) => <Page key={`modules-${pageIndex}`} footerLabel={footerLabel} pageNumber={pageIndex + 2} totalPages={totalPages} data-id="element-1637" data-mp="m95fwc30nhbs" data-mpi={pageIndex}>
            <div className="flex items-baseline justify-between border-b border-gray-200 pb-4" data-id="element-1638" data-mp="v77md75ir26g">
              <div data-id="element-1639" data-mp="q9pbhb7xycvt">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#30718D]" data-id="element-1640" data-mp="q738lovzwljx">
                  The pathway
                  {pageIndex > 0 ? ' (continued)' : ''}
                </div>
                <h2 className="mt-1 text-2xl font-bold text-gray-900" data-id="element-1641" data-mp="2s2n7th0xfnli">
                  Modules {pageIndex * MODULES_PER_PAGE + 1}–
                  {pageIndex * MODULES_PER_PAGE + page.length} of {items.length}
                </h2>
              </div>
              <span className="text-xs text-gray-500" data-id="element-1642" data-mp="yp56wx2z6jod">
                Take these in the order shown.
              </span>
            </div>

            <div className="mt-2" data-id="element-1643" data-mp="12p5jec6ikkss">
              {page.map((moduleId, indexOnPage) => {
          const module = moduleById(moduleId);
          if (!module) return null;
          return <article key={moduleId} className="border-b border-gray-100 py-6 last:border-b-0" data-id="element-1644" data-mp="fr2d84zhfyk8" data-mpi={indexOnPage}>
                    <div className="flex items-baseline gap-3" data-id="element-1645" data-mp="jsxmbvnnng8k">
                      <span className="text-lg font-bold text-[#DB3725]" data-id="element-1646" data-mp="2a4tthncbhgon">
                        {pageIndex * MODULES_PER_PAGE + indexOnPage + 1}
                      </span>
                      <div data-id="element-1647" data-mp="11x242a3zuyp9">
                        <h3 className="text-[17px] font-bold leading-snug text-gray-900" data-id="element-1648" data-mp="11wa4ypb6uiu8">
                          {module.name}
                        </h3>
                        {(showCategory || showDuration || showModuleId) && <p className="mt-0.5 text-xs text-gray-500" data-id="element-1649" data-mp="s5fjvo9ohpad">
                            {[showCategory ? module.category : null, showDuration ? formatHours(module.durationHours) : null, showModuleId ? module.courseId : null].filter(Boolean).join(' · ')}
                          </p>}
                      </div>
                    </div>

                    <dl className="mt-4 space-y-3 pl-7" data-id="element-1650" data-mp="1ynkszgu5cdjr">
                      <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1651" data-mp="1yo40s4q31lk2">
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1652" data-mp="275edmux6j2by">
                          Description
                        </dt>
                        <dd className="text-[13px] leading-relaxed text-gray-800" data-id="element-1653" data-mp="2fx6kkvmzvtbs">
                          {module.description}
                        </dd>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1654" data-mp="2kxud5lqvgi84">
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1655" data-mp="1o7jxlwxmvqk2">
                          Relevance
                        </dt>
                        <dd className="text-[13px] leading-relaxed text-gray-800" data-id="element-1656" data-mp="1x5dqexw6w22h">
                          {module.relevance}
                        </dd>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1657" data-mp="38mdibsemkevz">
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1658" data-mp="3dkoq7xld0fbk">
                          What you’ll learn
                        </dt>
                        <dd data-id="element-1659" data-mp="2874clwvv2v5n">
                          <ul className="space-y-1" data-id="element-1660" data-mp="2d81v6aks9r7o">
                            {module.outcomes.map(outcome => <li key={outcome} className="flex gap-2 text-[13px] leading-relaxed text-gray-800" data-id="element-1661" data-mp="2hyrzdkhh3o0s">
                                <span aria-hidden="true" className="mt-[7px] h-1 w-1 flex-shrink-0 rounded-full bg-[#30718D]" data-id="element-1662" data-mp="1pbe73zopzgn0" />
                                {outcome}
                              </li>)}
                          </ul>
                        </dd>
                      </div>
                      {showTopics && <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1663" data-mp="3epas72aqxn0k">
                          <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1664" data-mp="3erdou56emee9">
                            Topics
                          </dt>
                          <dd className="text-[13px] leading-relaxed text-gray-800" data-id="element-1665" data-mp="3juoaewaks06f">
                            {module.topics.join(' · ')}
                          </dd>
                        </div>}
                    </dl>
                  </article>;
        })}
            </div>
          </Page>)}
    </div>;
}